import React, { useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Rich reply / note editor backing a ticket thread.
 *
 * Why not just a <Textarea>? Engineers routinely paste screenshots directly
 * into the composer from the clipboard. The old text-only control couldn't
 * accept them, so the frontend lost the image entirely and the recipient
 * never saw the context. This editor:
 *
 *   • Captures `paste` + `drop` image blobs, uploads them via
 *     POST /tickets/{id}/paste-images, and inserts an `<img>` tag at the
 *     caret pointing at the backend attachment URL.
 *   • Submits HTML (not plain text). The parent calls `getHTML()` to pull
 *     the current content and `getText()` for the search-friendly fallback.
 *   • Draft-saves against the same key the Textarea used before so an
 *     engineer's half-written reply still survives a reload.
 */
export const TicketReplyEditor = React.forwardRef(function TicketReplyEditor(
  { ticketId, value, onChange, onDraftChange, placeholder, dataTestId, minHeight = 160 },
  ref,
) {
  const divRef = useRef(null);
  const uploadingRef = useRef(0);
  // Guard so we don't fight the browser's own text-cursor placement by
  // calling innerHTML = value on every keystroke — only sync when the
  // parent-supplied `value` is meaningfully different from what the user
  // has typed. This matters because setting innerHTML resets the caret.
  const lastSyncedHtmlRef = useRef("");

  // --- lifecycle: sync external value into the contentEditable ------------
  useEffect(() => {
    if (!divRef.current) return;
    const current = divRef.current.innerHTML;
    if ((value || "") !== current && (value || "") !== lastSyncedHtmlRef.current) {
      divRef.current.innerHTML = value || "";
      lastSyncedHtmlRef.current = value || "";
    }
  }, [value]);

  // --- expose imperative helpers to the parent ----------------------------
  React.useImperativeHandle(ref, () => ({
    getHTML: () => divRef.current?.innerHTML || "",
    getText: () => (divRef.current?.innerText || "").trim(),
    focus: () => divRef.current?.focus(),
    isUploading: () => uploadingRef.current > 0,
    clear: () => {
      if (divRef.current) {
        divRef.current.innerHTML = "";
        lastSyncedHtmlRef.current = "";
      }
    },
  }));

  // --- paste / drop handlers ----------------------------------------------
  const uploadImage = useCallback(async (file) => {
    if (!file || !file.type?.startsWith("image/")) return null;
    uploadingRef.current += 1;
    try {
      const form = new FormData();
      form.append("file", file, file.name || "pasted.png");
      const r = await api.post(`/tickets/${ticketId}/paste-images`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload image");
      return null;
    } finally {
      uploadingRef.current -= 1;
    }
  }, [ticketId]);

  const insertImageAtCaret = useCallback((url, alt) => {
    const el = divRef.current;
    if (!el) return;
    // Rehydrate caret inside the editor if the user alt-tabbed away first
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      // No selection — append at the end
      el.appendChild(document.createElement("br"));
    }
    const img = document.createElement("img");
    img.src = url;
    img.alt = alt || "pasted image";
    img.style.maxWidth = "100%";
    img.style.height = "auto";
    img.style.borderRadius = "4px";
    img.style.margin = "4px 0";
    const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    if (range) {
      range.deleteContents();
      range.insertNode(img);
      // Move caret after the inserted image
      range.setStartAfter(img);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      el.appendChild(img);
    }
    syncOut();
  }, []);

  const onPaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const images = [];
    const hasHtml = Array.from(items).some((it) => it.type === "text/html");
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        images.push(item.getAsFile());
      }
    }
    if (images.length === 0) {
      // Let the browser handle plain-text / html paste as usual, but strip
      // scripts/styles from any pasted HTML on the next tick.
      if (hasHtml) {
        setTimeout(sanitiseEditorContent, 0);
      }
      return;
    }
    e.preventDefault();
    for (const file of images) {
      // Insert a placeholder immediately so the user sees feedback
      const placeholderUrl = URL.createObjectURL(file);
      insertImageAtCaret(placeholderUrl, "uploading…");
      const result = await uploadImage(file);
      if (result && result.url) {
        // Swap the placeholder (last img with that src) for the hosted URL
        const el = divRef.current;
        if (el) {
          const imgs = el.querySelectorAll(`img[src="${placeholderUrl}"]`);
          imgs.forEach((img) => {
            img.src = `${BACKEND_URL}${result.url}`;
            img.setAttribute("data-attachment-id", result.id);
            img.alt = result.name || "pasted image";
          });
          URL.revokeObjectURL(placeholderUrl);
          syncOut();
        }
      } else {
        // Upload failed — remove the broken preview so the thread isn't poisoned
        const el = divRef.current;
        if (el) {
          const imgs = el.querySelectorAll(`img[src="${placeholderUrl}"]`);
          imgs.forEach((img) => img.remove());
          syncOut();
        }
      }
    }
  }, [insertImageAtCaret, uploadImage]);

  const onDrop = useCallback(async (e) => {
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type?.startsWith("image/"));
    if (files.length === 0) return;
    e.preventDefault();
    for (const file of files) {
      const placeholderUrl = URL.createObjectURL(file);
      insertImageAtCaret(placeholderUrl, "uploading…");
      const result = await uploadImage(file);
      if (result && result.url) {
        const imgs = divRef.current?.querySelectorAll(`img[src="${placeholderUrl}"]`) || [];
        imgs.forEach((img) => {
          img.src = `${BACKEND_URL}${result.url}`;
          img.setAttribute("data-attachment-id", result.id);
        });
        URL.revokeObjectURL(placeholderUrl);
        syncOut();
      }
    }
  }, [insertImageAtCaret, uploadImage]);

  // Strip script/style/onclick from pasted HTML — belt-and-braces guard, the
  // backend re-sanitises on store too.
  const sanitiseEditorContent = useCallback(() => {
    const el = divRef.current;
    if (!el) return;
    el.querySelectorAll("script,style,iframe,object,embed").forEach((n) => n.remove());
    el.querySelectorAll("*").forEach((n) => {
      for (const attr of Array.from(n.attributes)) {
        if (attr.name.toLowerCase().startsWith("on")) n.removeAttribute(attr.name);
      }
    });
    syncOut();
  }, []);

  const syncOut = useCallback(() => {
    if (!divRef.current) return;
    const html = divRef.current.innerHTML;
    const text = (divRef.current.innerText || "").trim();
    lastSyncedHtmlRef.current = html;
    onChange?.({ html, text });
    onDraftChange?.(text, html);
  }, [onChange, onDraftChange]);

  return (
    <div
      ref={divRef}
      contentEditable={true}
      role="textbox"
      aria-multiline="true"
      spellCheck={true}
      lang="en"
      autoCorrect="on"
      autoCapitalize="sentences"
      data-placeholder={placeholder || ""}
      data-testid={dataTestId}
      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 overflow-y-auto empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-1"
      style={{ minHeight }}
      onInput={syncOut}
      onPaste={onPaste}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
      suppressContentEditableWarning
    />
  );
});

export default TicketReplyEditor;
